/**
 * The one place the app talks to the BlueView API.
 *
 * WHY EVERY CALL GOES THROUGH HERE
 * The same three things are needed on every request and are easy to forget on
 * one of them: the session cookie has to be sent, a non-2xx has to become a
 * useful Error rather than a silently-ignored Response, and a 401 has to be
 * distinguishable from a real failure so the app can send the person back to
 * the sign-in screen instead of showing them "something went wrong".
 *
 * The cookie is httpOnly, so there is no token to attach by hand — the browser
 * carries it as long as `credentials` is set, which is exactly why it is set
 * here once instead of at forty call sites.
 */

/**
 * Where the API lives.
 *
 * Same origin in production, where nginx serves the app and proxies /api to
 * the container. Only a separate dev server (npm start on :3000) needs the
 * override, and it must then also be listed in the API's CORS_ORIGINS —
 * a cookie will not travel cross-origin otherwise.
 */
const API_BASE = (process.env.REACT_APP_API_BASE ?? '/api').replace(/\/$/, '');

/** A failed request, carrying enough to decide what to do about it. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** True when the session is missing or expired, not merely forbidden. */
    readonly unauthenticated: boolean,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Anyone who needs to react to the session going away — the auth context. */
type UnauthorisedHandler = () => void;
let onUnauthorised: UnauthorisedHandler | null = null;

export function setUnauthorisedHandler(handler: UnauthorisedHandler | null): void {
  onUnauthorised = handler;
}

async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    // FastAPI puts the message in `detail`; a validation failure puts a list
    // of field errors there instead, which is not something to show raw.
    const detail = body?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : '';
      return field ? `${field}: ${first.msg}` : String(first.msg ?? 'Invalid request');
    }
  } catch {
    // Not JSON — an nginx error page, or the container being restarted.
  }
  return response.status >= 500
    ? 'The server could not complete that request.'
    : `Request failed (${response.status})`;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      // Sends and accepts the session cookie. Without it every request is
      // anonymous and the whole API answers 401.
      credentials: 'include',
      headers: {
        // FormData sets its own Content-Type, and it has to: the header
        // carries the multipart boundary, which only the browser knows.
        // Declaring JSON over it makes the server reject the upload.
        ...(init.body && !(init.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch (error) {
    // fetch only rejects when the request never completed — offline, DNS,
    // TLS. Worth its own message: "failed to fetch" tells a user nothing.
    throw new ApiError('Could not reach the server. Check your connection.', 0, false);
  }

  if (response.status === 401) {
    // Announced once, centrally, so a stale tab drops back to sign-in rather
    // than every screen inventing its own handling.
    onUnauthorised?.();
    throw new ApiError(await readError(response), 401, true);
  }

  if (!response.ok) {
    throw new ApiError(await readError(response), response.status, false);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  /**
   * A file upload. Separate from `post` because the body must NOT be
   * JSON-stringified and must NOT carry a Content-Type of our choosing.
   *
   * There is no progress callback: `fetch` reports download progress but not
   * upload progress, and inventing a fake one for a 200 MB geodatabase would
   * be worse than a plain spinner that is honest about not knowing.
   */
  postForm: <T>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', body: form }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
